import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import WebApp from '@twa-dev/sdk';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Сначала пробуем получить тему из Telegram
    if (typeof window !== 'undefined' && WebApp.colorScheme) {
      return WebApp.colorScheme as Theme;
    }
    
    // Затем проверяем localStorage
    const stored = localStorage.getItem('theme') as Theme | null;
    if (stored) return stored;
    
    // Затем системные настройки
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    
    // Применяем тему
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Сохраняем в localStorage
    localStorage.setItem('theme', theme);
    
    // Синхронизируем с Telegram HeaderColor
    if (WebApp.setHeaderColor) {
      WebApp.setHeaderColor(theme === 'dark' ? '#1c1c1d' : '#ffffff');
    }
  }, [theme]);

  // Слушаем изменения темы Telegram
  useEffect(() => {
    const handleThemeChange = () => {
      if (WebApp.colorScheme) {
        setThemeState(WebApp.colorScheme as Theme);
      }
    };

    WebApp.onEvent('themeChanged', handleThemeChange);
    
    return () => {
      WebApp.offEvent('themeChanged', handleThemeChange);
    };
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
